import {
  SEAT_CLASS_SPECS,
  type AircraftCategory,
  type AircraftType,
  type CabinConfiguration,
} from '../models';
import { calculateRecommendedGalleys, floorUnitsPerSeat } from './cabinMath';
import { hasBlockingIssues, validateCabinConfiguration } from './cabinValidator';

/**
 * Share of a type's floor units given to each premium class in the starting
 * layout. Economy takes whatever is left.
 */
interface ClassMixShares {
  readonly premiumEconomy: number;
  readonly business: number;
  readonly first: number;
}

const DEFAULT_CLASS_MIX: Readonly<Record<AircraftCategory, ClassMixShares>> = {
  TURBOPROP_REGIONAL: { premiumEconomy: 0, business: 0, first: 0 },
  REGIONAL_JET: { premiumEconomy: 0, business: 0.12, first: 0 },
  NARROWBODY: { premiumEconomy: 0, business: 0.12, first: 0 },
  WIDEBODY: { premiumEconomy: 0.1, business: 0.18, first: 0 },
  SUPERJUMBO: { premiumEconomy: 0.1, business: 0.16, first: 0.06 },
  SUPERSONIC: { premiumEconomy: 0.6, business: 0.4, first: 0 },
};

const ECONOMY_SPEC = SEAT_CLASS_SPECS.ECONOMY;
const BUSINESS_SPEC = SEAT_CLASS_SPECS.BUSINESS;

function seatsForShare(floorUnits: number, share: number, unitsPerSeat: number): number {
  return share > 0 ? Math.max(1, Math.floor((floorUnits * share) / unitsPerSeat)) : 0;
}

/**
 * A sensible, always-valid starting layout for a type: class mix by aircraft
 * category, default pitches, the recommended galley count and no optional
 * amenities. Total seats never exceed the certified limit or the floor budget.
 */
export function createDefaultCabinConfiguration(type: AircraftType): CabinConfiguration {
  const mix = DEFAULT_CLASS_MIX[type.category];
  const budget = type.cabinFloorUnits;

  const economyUnits = floorUnitsPerSeat('ECONOMY', ECONOMY_SPEC.defaultPitchInches);
  const businessUnits = floorUnitsPerSeat('BUSINESS', BUSINESS_SPEC.defaultPitchInches);

  const firstSeats = seatsForShare(budget, mix.first, SEAT_CLASS_SPECS.FIRST.unitsPerSeat);
  const businessSeats = seatsForShare(budget, mix.business, businessUnits);
  const premiumEconomySeats = seatsForShare(
    budget,
    mix.premiumEconomy,
    SEAT_CLASS_SPECS.PREMIUM_ECONOMY.unitsPerSeat
  );

  const premiumUnits =
    firstSeats * SEAT_CLASS_SPECS.FIRST.unitsPerSeat +
    businessSeats * businessUnits +
    premiumEconomySeats * SEAT_CLASS_SPECS.PREMIUM_ECONOMY.unitsPerSeat;

  let economySeats = Math.max(0, Math.floor((budget - premiumUnits) / economyUnits));

  // Respect the certified passenger limit by trimming Economy first.
  const premiumSeats = firstSeats + businessSeats + premiumEconomySeats;
  const certifiedRoom = Math.max(0, type.maxCertifiedSeats - premiumSeats);
  economySeats = Math.min(economySeats, certifiedRoom);

  const layout: CabinConfiguration = {
    economySeats,
    economyPitchInches: ECONOMY_SPEC.defaultPitchInches,
    premiumEconomySeats,
    businessSeats,
    businessPitchInches: BUSINESS_SPEC.defaultPitchInches,
    firstSeats,
    galleyCount: 0,
    wifiEquipped: false,
    inSeatPower: false,
  };

  return { ...layout, galleyCount: calculateRecommendedGalleys(layout) };
}

/** Seats Business gets per point of the pair's business share, before the cap below. */
export const MARKET_CABIN_BUSINESS_SEAT_RATIO = 0.5;
/**
 * Most of the finished cabin's seats given to Business. A short-haul Business seat takes 2.0 floor units, so a
 * cabin cut for business travellers still keeps most of its seats in Economy.
 */
export const MARKET_CABIN_MAX_BUSINESS_SEAT_SHARE = 0.2;
/** Short-haul Business seat pitch: a wide recliner rather than a lie-flat bed. */
export const MARKET_CABIN_BUSINESS_PITCH_INCHES = 60;

/**
 * The default layout re-cut for a business-heavy market: Business seats scaled to the pair's business share
 * (half the share of the seats, at most a fifth of the finished cabin) on a short-haul recliner pitch, Economy
 * filling the rest of the floor. Never fewer Business seats than the default, and the default itself when the
 * cut would not validate.
 */
export function createMarketCabinConfiguration(type: AircraftType, businessShare: number): CabinConfiguration {
  const base = createDefaultCabinConfiguration(type);
  if (!(businessShare > 0)) return base;
  const baseSeats = base.economySeats + base.premiumEconomySeats + base.businessSeats + base.firstSeats;
  const wanted = Math.round(MARKET_CABIN_BUSINESS_SEAT_RATIO * Math.min(1, businessShare) * baseSeats);

  const businessUnits = floorUnitsPerSeat('BUSINESS', MARKET_CABIN_BUSINESS_PITCH_INCHES);
  const economyUnits = floorUnitsPerSeat('ECONOMY', base.economyPitchInches);
  const otherUnits =
    base.firstSeats * SEAT_CLASS_SPECS.FIRST.unitsPerSeat +
    base.premiumEconomySeats * SEAT_CLASS_SPECS.PREMIUM_ECONOMY.unitsPerSeat;
  const economyFor = (businessSeats: number): number => {
    const room = type.cabinFloorUnits - otherUnits - businessSeats * businessUnits;
    const certifiedRoom = Math.max(0, type.maxCertifiedSeats - businessSeats - base.premiumEconomySeats - base.firstSeats);
    return Math.min(Math.max(0, Math.floor(room / economyUnits)), certifiedRoom);
  };

  // The most Business seats up to `wanted` that stay within the share cap of the finished cabin.
  let businessSeats = wanted;
  while (businessSeats > base.businessSeats) {
    const total = businessSeats + economyFor(businessSeats) + base.premiumEconomySeats + base.firstSeats;
    if (businessSeats <= MARKET_CABIN_MAX_BUSINESS_SEAT_SHARE * total) break;
    businessSeats -= 1;
  }
  if (businessSeats <= base.businessSeats) return base;

  const layout: CabinConfiguration = {
    ...base,
    economySeats: economyFor(businessSeats),
    businessSeats,
    businessPitchInches: MARKET_CABIN_BUSINESS_PITCH_INCHES,
  };
  const withGalleys = { ...layout, galleyCount: calculateRecommendedGalleys(layout) };
  return hasBlockingIssues(validateCabinConfiguration(type, withGalleys).issues) ? base : withGalleys;
}
